import { useSliderState } from "./widgets/Chat/components/slider/SliderState";

/**
 * URL-driven fixtures for photographing widget states from the preview
 * (e.g. ?fixture=pools-links on /chat or shadow-test.html).
 *
 * The preview is a PRODUCTION build pointed at dev data, so the NODE_ENV
 * dev hook in index.tsx does not exist there. The flag we gate on is
 * REACT_APP_IS_PROD: "no" means "this is a preview/dev deployment".
 */
export const isFixtureEnabled = (): boolean =>
  process.env.REACT_APP_IS_PROD === "no";

// Restricted to safe filenames: the value lands directly in a fetch URL.
export const getFixtureName = (): string | null => {
  if (!isFixtureEnabled()) return null;
  const name = new URLSearchParams(window.location.search).get("fixture");
  return name && /^[a-z0-9_-]+$/.test(name) ? name : null;
};

/**
 * The view the preview page should open on (?view=immersive): right after the
 * artifacts load, the fullscreen surface sits on the first item, so the
 * screenshot photographs the actual case instead of the docked card.
 */
export const getFixtureView = (): string | null => {
  if (!isFixtureEnabled()) return null;
  const view = new URLSearchParams(window.location.search).get("view");
  return view === "immersive" ? view : null;
};

export async function loadFixtures(fetchFn: typeof fetch = fetch) {
  const name = getFixtureName();
  if (!name) return;

  let templates: any[] | null = null;
  try {
    const response = await fetchFn(`/fixtures/${name}.json`);
    if (!response.ok) {
      console.error(`Fixture "${name}" could not be loaded.`);
      return;
    }
    templates = await response.json();
  } catch (error) {
    console.error(`Fixture "${name}" could not be loaded.`, error);
    return;
  }

  if (!Array.isArray(templates) || !templates.length) {
    console.error(`Fixture "${name}" is not a non-empty template array.`);
    return;
  }

  // Same group-and-open path as a live assistant reply, so the fixture
  // produces exactly the state the widget would show in production. The
  // fullscreen surface is expanded only when the preview asks for it, so the
  // default page still photographs the docked card.
  useSliderState.getState().openSlider("fixture", "fixture", templates, false);
  if (getFixtureView() === "immersive") {
    useSliderState.getState().setExpanded(true);
  }
}
